import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { Button, Modal, Input, Divider, message, Upload } from "antd";
import Cookies from "js-cookie";
import { UploadOutlined } from "@ant-design/icons";
import { backendURL } from "../Globals";

const Card = ({ name, href, logo, onDelete, showAdminPanel }) => {
  return (
    <div className="card-container">
      <a href={href} className="card" target="_blank" rel="noopener noreferrer">
        {logo ? (
          <img src={`${backendURL}/logos/${logo}`} alt={name} className="editor-logo" />
        ) : (
          <div className="card-content">{name}</div>
        )}
      </a>
      {showAdminPanel && (
        <button className="delete-button" onClick={onDelete}>🗑️ Eliminar</button>
      )}
    </div>
  );
};


const AboutComponent = () => {
  const location = useLocation();
  const [publishers, setPublishers] = useState([]);
  // 🔹 Estado para el nuevo editor
const [newPublisher, setNewPublisher] = useState({ name: "", href: "", logo: null });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // 🔹 Estados para los textos editables
  const [introText, setIntroText] = useState("");
  const [biblioText, setBiblioText] = useState("");

  // 🔹 Sincronizar isLoggedIn con el evento storage (logout desde cualquier tab o mismo tab)
  useEffect(() => {
    const syncAuth = () => {
      const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");
      setIsLoggedIn(!!apiKey);
      if (!apiKey) setShowAdminPanel(false);
    };
    window.addEventListener("storage", syncAuth);
    return () => window.removeEventListener("storage", syncAuth);
  }, []);

  useEffect(() => {
    const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");
    setIsLoggedIn(!!apiKey);

    // Obtener publishers
    axios.get(`${backendURL}/publishers`)
      .then(response => setPublishers(response.data))
      .catch(error => console.error("Error fetching publishers:", error));

    // Obtener los textos de about
    axios.get(`${backendURL}/about`)
      .then(response => {
        const intro = response.data.find(item => item.section_name === "intro");
        const biblio = response.data.find(item => item.section_name === "biblioguia");
        if (intro) setIntroText(intro.content);
        if (biblio) setBiblioText(biblio.content);
      })
      .catch(error => console.error("Error fetching about info:", error));
  }, [location.pathname]);
  const handleDelete = (id) => {
    Modal.confirm({
      title: "¿Estás seguro de eliminar este editor?",
      content: "Esta acción no se puede deshacer.",
      okText: "Sí, eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");
        if (!apiKey) return;

        try {
          await axios.delete(`${backendURL}/publishers/${id}`, {
            headers: { Authorization: `Bearer ${apiKey}` }
          });
          setPublishers(publishers.filter(p => p.id !== id));
        } catch (error) {
          console.error("Error deleting publisher:", error);
        }
      }
    });
  };

  // 🔹 Manejar subida de imagen
const handleUpload = ({ file }) => {
  setNewPublisher({ ...newPublisher, logo: file });
};

// 🔹 Añadir un nuevo editor
const handleAddPublisher = async () => {
  if (!newPublisher.name || !newPublisher.href || !newPublisher.logo) {
    message.error("Todos los campos son obligatorios");
    return;
  }

  const formData = new FormData();
  formData.append("name", newPublisher.name);
  formData.append("href", newPublisher.href);
  formData.append("logo", newPublisher.logo);

  const apiKey = Cookies.get("apiKey") || localStorage.getItem("apiKey");

  try {
    const response = await axios.post(`${backendURL}/publishers`, formData, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
        "Content-Type": "multipart/form-data"
      }
    });

    setPublishers([...publishers, response.data]); // Añadir a la lista
    message.success("Editor añadido correctamente");
    setNewPublisher({ name: "", href: "", logo: null }); // Reiniciar formulario
  } catch (error) {
    console.error("Error al añadir editor:", error);
    message.error("Error al añadir editor");
  }
};

  return (
    <div>
      {/* Cabecera gris — ancho completo */}
      <div style={{ paddingTop: 28, paddingBottom: 4, textAlign: 'center', background: '#fafafa' }}>
        <div style={{
          fontSize: 22, fontWeight: 700, color: '#1a1a1a',
          letterSpacing: -0.3, lineHeight: 1.3,
          fontFamily: "'Inter','Segoe UI',sans-serif",
          padding: '0 24px',
        }}>
          Acuerdos transformativos
        </div>
        <div style={{ fontSize: 13, color: '#888', marginTop: 6, paddingBottom: 16 }}>
          Qué son, cómo funcionan y con qué editoriales tiene acuerdo la Universidad de Oviedo
        </div>
      </div>

    <div className="about-container">
      <p className="about-text">
        Los acuerdos transformativos son contratos negociados entre instituciones (bibliotecas y consorcios) y
        editoriales en los que el gasto en suscripciones se redirige para cubrir los costes de publicación en
        acceso abierto del personal investigador de la institución. El objetivo es doble: racionalizar el gasto
        institucional en publicación científica y acelerar la transición desde el modelo de suscripción hacia el
        acceso abierto. En la práctica, cada año se dispone de una cantidad limitada de APCs y solo son elegibles
        las revistas incluidas en la selección pactada con cada editorial.
      </p>

      <p className="about-text" style={{ marginTop: '0.8cm', marginBottom: '1cm', color: '#555' }}>
        <strong>Acuerdos firmados por la Universidad de Oviedo:</strong>
      </p>

      <div className="card-grid">
        {publishers.map(publisher => (
          <Card
            key={publisher.id}
            name={publisher.name}
            href={publisher.href}
            logo={publisher.logo_url}
            onDelete={() => handleDelete(publisher.id)}
            showAdminPanel={isLoggedIn && showAdminPanel}
          />
        ))}
      </div>

      {/* Biblioguía */}
      <div style={{ marginTop: "48px" }}>
        <p className="about-text">
          Consulta información ampliada sobre los acuerdos transformativos en nuestra{" "}
          <a href="https://uniovi.libguides.com/acuerdostransformativos" target="_blank" rel="noopener noreferrer">Biblioguía</a>.
        </p>
      </div>

      {/* 🔹 Mostrar botón para activar/desactivar administración */}
      {isLoggedIn && (
        <div style={{ marginTop: "30px" }}>
          {/* 🔹 Formulario para añadir un nuevo editor */}
{isLoggedIn && showAdminPanel && (
  <div className="add-publisher-form">
    <Divider />
    <h3>Añadir un nuevo editor</h3>
    <Input
      placeholder="Nombre del editor"
      value={newPublisher.name}
      onChange={(e) => setNewPublisher({ ...newPublisher, name: e.target.value })}
    />
    <Input
      placeholder="URL del editor"
      value={newPublisher.href}
      onChange={(e) => setNewPublisher({ ...newPublisher, href: e.target.value })}
    />
    <Upload beforeUpload={() => false} onChange={handleUpload} showUploadList={false}>
      <Button icon={<UploadOutlined />}>Subir logo</Button>
    </Upload>
    <Button type="primary" onClick={handleAddPublisher} style={{ marginTop: "10px" }}>
      Añadir Editor
    </Button>
  </div>
)}
          <Button type="dashed" onClick={() => setShowAdminPanel(!showAdminPanel)}>
            {showAdminPanel ? "Ocultar Administración" : "Administrar Editores"}
          </Button>
        </div>
      )}
    </div>
    </div>
  );
};

export default AboutComponent;

